import React from 'react';
import {
  List, ListItemButton, ListItemIcon, ListItemText, Divider, Typography, Chip, Box,
} from '@mui/material';
import {
  Dashboard, People, Code, Settings, Security, Api, Analytics, Logout, SwapHoriz,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const menuConfig = [
  // Available to all authenticated users
  { label: 'Dashboard', icon: <Dashboard />, path: '/', minRole: 'user' },
  { label: 'API Keys', icon: <Api />, path: '/api-keys', minRole: 'user' },

  // Developer and above
  { label: 'API Explorer', icon: <Code />, path: '/api-explorer', minRole: 'user' },
  { label: 'Analytics', icon: <Analytics />, path: '/analytics', minRole: 'user' },

  // Admin only
  { divider: true, minRole: 'admin', label: 'Administration' },
  { label: 'Admin Dashboard', icon: <Dashboard />, path: '/admin/dashboard', minRole: 'admin' },
  { label: 'User Management', icon: <People />, path: '/admin/users', minRole: 'admin' },
  { label: 'Roles & Permissions', icon: <Security />, path: '/admin/roles', minRole: 'admin' },
  { label: 'Detach Requests', icon: <SwapHoriz />, path: '/admin/detach-requests', minRole: 'admin' },
  { label: 'Settings', icon: <Settings />, path: '/admin/settings', minRole: 'admin' },
];

const ROLE_HIERARCHY = { user: 0, admin: 2 };

const roleColors = {
  user: 'default',
  admin: 'error',
};

export default function AdminMenu() {
  const { role, user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const userLevel = ROLE_HIERARCHY[role] ?? -1;

  const visibleItems = menuConfig.filter(
    (item) => userLevel >= (ROLE_HIERARCHY[item.minRole] ?? 99)
  );

  return (
    <Box sx={{ width: 260, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* User info */}
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" noWrap>{user?.email || user?.username}</Typography>
        <Chip label={role?.toUpperCase()} size="small" color={roleColors[role] || 'default'} sx={{ mt: 0.5 }} />
      </Box>
      <Divider />

      {/* Menu items */}
      <List sx={{ flex: 1 }}>
        {visibleItems.map((item, idx) => {
          if (item.divider) {
            return (
              <React.Fragment key={`div-${idx}`}>
                <Divider sx={{ mt: 1, mb: 0.5 }} />
                <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>
                  {item.label}
                </Typography>
              </React.Fragment>
            );
          }
          return (
            <ListItemButton
              key={item.path}
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>

      <Divider />
      <List>
        <ListItemButton onClick={signOut}>
          <ListItemIcon><Logout /></ListItemIcon>
          <ListItemText primary="Sign Out" />
        </ListItemButton>
      </List>
    </Box>
  );
}
